// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useNavigate } from 'react-router-dom'
import { ModuleShell } from '@/components/ui/ModuleShell'
import { InfoBlock } from '@/components/ui/InfoBlock'
import { NextStepBlock, type NextStepAction } from '@/components/process/NextStepBlock'
import { ROUTES } from '@/lib/constants/routes'
import { navState } from '@/lib/returnNav'

/**
 * Uw situatie ("Waar staat u nu?") - persoonlijke procesbegeleiding.
 * Geen kenniscentrum: elke situatie brengt de gebruiker in één tik naar de
 * bestaande module die erbij hoort. De informatie begeleidt (wat kunt u nu
 * regelen, wie moet het weten, wanneer opnieuw controleren) en beschrijft geen
 * ziektes. Wilsbekwaamheid is de rode draad.
 */
export function LevenslijnPage() {
  const navigate = useNavigate()
  const go = (route: string): NextStepAction['onClick'] => () => navigate(route, navState(ROUTES.LEVENSLIJN, 'Waar staat u nu?'))

  return (
    <ModuleShell
      title="Waar staat u nu?"
      description="Uw situatie. Kies wat bij u past - WilsApp brengt u in één tik naar de juiste stap. U leest geen losse informatie, maar wordt geholpen bij wat u nu kunt regelen."
    >
      {/* Wilsbekwaamheid prominent + neutrale praktijkles, niet ingeklapt */}
      <div className="rounded-xl border border-sage-200 bg-sage-50 px-4 py-3.5">
        <p className="text-sm font-semibold text-sage-800">WilsApp helpt u zolang u zelf keuzes kunt maken.</p>
        <p className="text-xs text-sage-700 mt-1.5 leading-relaxed">
          Sommige aandoeningen ontwikkelen zich sneller dan verwacht. Door belangrijke wensen tijdig vast te
          leggen en af en toe te controleren, geeft u uzelf en uw naasten meer duidelijkheid. WilsApp
          beoordeelt nooit of iemand wilsbekwaam is; het helpt u alleen om op tijd overzicht te houden.
        </p>
      </div>

      {/* 1 - Ik wil mijn zaken goed regelen */}
      <NextStepBlock
        title="Ik wil mijn zaken goed regelen"
        intro="Een rustig begin: leg uw persoonlijke gegevens vast, noteer waar belangrijke documenten liggen en bepaal wie u vertrouwt."
        actions={[
          { icon: '👤', label: 'Persoonlijke gegevens', onClick: go(ROUTES.INTAKE) },
          { icon: '📁', label: 'Documentwijzer',        onClick: go(ROUTES.DOCUMENTS) },
          { icon: '🤝', label: 'Vertrouwenspersoon',    onClick: go(ROUTES.TRUSTED_PERSON) },
        ]}
      />

      {/* 2 - Ik heb een gezin */}
      <NextStepBlock
        title="Ik heb een gezin en wil mijn naasten ontzorgen"
        intro="Regel zaken die voor uw partner en kinderen belangrijk zijn, en leg persoonlijke berichten vast voor later."
        actions={[
          { icon: '❤️', label: 'Partner',                          onClick: go(ROUTES.PARTNER) },
          { icon: '🧒', label: 'Kinderen, voogdij & executeur',    onClick: go(ROUTES.NOTARIAL) },
          { icon: '✉️', label: 'Persoonlijk afscheid & berichten', onClick: go(ROUTES.WISHES_FAREWELL) },
        ]}
      />

      {/* 3 - Ik heb een diagnose gekregen */}
      <div className="flex flex-col gap-3">
        <InfoBlock title="Wat kunt u nu alvast regelen?" variant="blue" defaultOpen={false}>
          <p>Bij aandoeningen zoals dementie of Parkinson kan het na verloop van tijd moeilijker worden om zelf keuzes te maken of wensen kenbaar te maken. Wat is dan verstandig om nu te doen?</p>
          <p><strong>Leg vast wat belangrijk is:</strong> wat u medisch wel en niet wilt, wie namens u mag handelen, en waar uw documenten te vinden zijn.</p>
          <p><strong>Zorg dat de juiste mensen het weten:</strong> uw naasten, uw vertrouwenspersoon en uw huisarts.</p>
          <p><strong>Voorbeeld:</strong> als u later moeite krijgt met spreken of beslissen, helpt het wanneer uw naasten en zorgverleners weten wat u eerder heeft vastgelegd.</p>
        </InfoBlock>
        <InfoBlock title="Parkinson - wat is verstandig om vast te leggen?" variant="neutral" defaultOpen={false}>
          <p>Geen uitleg over de ziekte, maar praktische voorbereiding. Denk aan:</p>
          <p>• hoe u het liefst communiceert als spreken of schrijven moeilijker wordt;<br/>
             • wie namens u beslist (vertegenwoordiging);<br/>
             • uw medische wensen, en wie uw huisarts en specialist zijn;<br/>
             • afspraken met uw partner en gezin over de dagelijkse regie;<br/>
             • waar uw documenten staan en hoe uw digitale diensten worden afgehandeld.</p>
          <p>Bespreek dit met uw huisarts en kijk het af en toe opnieuw na.</p>
        </InfoBlock>
        <InfoBlock title="Dementie op jonge leeftijd - extra aandachtspunten" variant="neutral" defaultOpen={false}>
          <p>Op jongere leeftijd spelen vaak ook werk, inkomen, hypotheek, onderneming, verzekeringen en de zorg voor kinderen.</p>
          <p>Het helpt om deze zaken tijdig te ordenen. U vindt ze terug bij Partner, Financieel overzicht, Onderneming en Digitale nalatenschap in WilsApp. Bespreek met uw naasten wie waarvan op de hoogte moet zijn.</p>
        </InfoBlock>
        <NextStepBlock
          title="Ik heb een diagnose gekregen"
          intro="Leg uw medische wensen vast, regel vertegenwoordiging en zorg dat uw documenten vindbaar zijn."
          actions={[
            { icon: '🩺', label: 'Medische wensen',            onClick: go(ROUTES.WISHES_MEDICAL) },
            { icon: '⚖️', label: 'Levenstestament',            onClick: go(ROUTES.NOTARIAL) },
            { icon: '🤝', label: 'Vertegenwoordiging regelen', onClick: go(ROUTES.WISHES_MEDICAL) },
            { icon: '📁', label: 'Documentwijzer',             onClick: go(ROUTES.DOCUMENTS) },
          ]}
        />
      </div>

      {/* 4 - Mijn gezondheid verandert */}
      <div className="flex flex-col gap-3">
        <InfoBlock title="Bespreek wijzigingen met uw huisarts" variant="neutral" defaultOpen={false}>
          <p>Wanneer uw gezondheid verandert, kan het verstandig zijn om uw wensen opnieuw met uw huisarts te bespreken.</p>
          <p>Het helpt om wijzigingen opnieuw te bespreken, ervoor te zorgen dat uw huisarts over de meest recente verklaringen beschikt, en vast te leggen wie uw vertegenwoordiger of contactpersoon is. WilsApp geeft hierbij geen medisch advies.</p>
        </InfoBlock>
        <NextStepBlock
          title="Mijn gezondheid verandert"
          intro="Herzie uw medische wensen, houd uw vertrouwenspersoon actueel en bespreek belangrijke keuzes met uw huisarts."
          actions={[
            { icon: '🩺', label: 'Medische wensen herzien',           onClick: go(ROUTES.WISHES_MEDICAL) },
            { icon: '📄', label: 'Behandelgrenzen & niet-reanimeren', onClick: go(ROUTES.WISHES_MEDICAL) },
            { icon: '🤝', label: 'Vertrouwenspersoon bijwerken',      onClick: go(ROUTES.TRUSTED_PERSON) },
            { icon: 'ℹ️', label: 'Informatie over euthanasie',       onClick: go(ROUTES.EUTHANASIE_INFO) },
          ]}
          disclaimer="WilsApp geeft geen medisch of juridisch advies. Bespreek uw keuzes met uw huisarts of een andere bevoegde professional."
        />
      </div>

      {/* 5 - Ik wil mijn wensen actualiseren */}
      <NextStepBlock
        title="Ik wil mijn wensen actualiseren"
        intro="Loop uw controlepunten langs en controleer of uw documenten nog kloppen en vindbaar zijn."
        actions={[
          { icon: '✅', label: 'Controlepunten', onClick: go(ROUTES.CONTROL_CENTER) },
          { icon: '📁', label: 'Documentwijzer', onClick: go(ROUTES.DOCUMENTS) },
        ]}
      />
    </ModuleShell>
  )
}
